/**
 * Per-browser guest identity for the Starblast demo.
 *
 * The PlayServ SDK has no login phase: `userId` is supplied by the game
 * client at config time and sent in HandshakeRequest. We generate one on
 * first visit and cache it in localStorage so reloads keep the same id.
 *
 * `guestName` is purely cosmetic — used for on-canvas display until a real
 * user-profile model exists.
 *
 * Storage layout (window.localStorage):
 *   starblast.userId     — UUID, sent as `userId` in PlayServConfig.
 *   starblast.guestName  — auto-generated display name (`Pilot_NNNNNN`).
 *
 * crypto.randomUUID() requires a secure context; Vite's localhost dev server
 * qualifies. Production over plain http would not — add a fallback then.
 */

const STORAGE_KEYS = {
  userId: 'starblast.userId',
  guestName: 'starblast.guestName',
} as const;

export interface GuestIdentity {
  userId: string;
  guestName: string;
}

function generateGuestName(): string {
  const suffix = Math.floor(Math.random() * 900_000 + 100_000);
  return `Pilot_${suffix}`;
}

// PSV-1902 UX — readable suggested callsigns for the entry screen: the
// player can just hit PLAY with a decent name instead of facing an empty
// input (or a Pilot_483920). Combinations stay within the server's
// pilot-name rule (3-16 chars, Latin/digits).
const CALLSIGN_ADJ = [
  'Nova', 'Iron', 'Ghost', 'Solar', 'Crimson', 'Turbo', 'Lunar', 'Void',
  'Storm', 'Hyper', 'Astro', 'Neon', 'Cosmic', 'Rogue', 'Zero',
] as const;
const CALLSIGN_NOUN = [
  'Fox', 'Comet', 'Falcon', 'Viper', 'Raider', 'Hawk', 'Nomad', 'Wolf',
  'Drifter', 'Pilot', 'Racer', 'Hunter', 'Spark', 'Blade',
] as const;

/** A fresh readable callsign, e.g. "NovaFox" or "VoidViper7". */
export function generateCallsign(): string {
  const adj = CALLSIGN_ADJ[Math.floor(Math.random() * CALLSIGN_ADJ.length)]!;
  const noun = CALLSIGN_NOUN[Math.floor(Math.random() * CALLSIGN_NOUN.length)]!;
  const withDigit = Math.random() < 0.5;
  const name = `${adj}${noun}${withDigit ? Math.floor(Math.random() * 90 + 10) : ''}`;
  return name.slice(0, 16);
}

export function readGuestIdentity(): GuestIdentity {
  // PSV-1726 QA aid: `?pilot=<tag>` gives this TAB its own identity
  // (sessionStorage-scoped). Needed for single-machine multiplayer tests —
  // incognito windows share ONE localStorage, so two of them silently
  // became the same player and read as "multiplayer is broken".
  const pilotTag = new URLSearchParams(window.location.search).get('pilot');
  if (pilotTag !== null && pilotTag !== '') {
    const key = `starblast.pilot.${pilotTag}.userId`;
    let tabUserId = sessionStorage.getItem(key);
    if (!tabUserId) {
      tabUserId = crypto.randomUUID();
      sessionStorage.setItem(key, tabUserId);
    }
    return { userId: tabUserId, guestName: `Pilot_${pilotTag}` };
  }

  let userId = localStorage.getItem(STORAGE_KEYS.userId);
  if (!userId) {
    userId = crypto.randomUUID();
    localStorage.setItem(STORAGE_KEYS.userId, userId);
  }
  let guestName = localStorage.getItem(STORAGE_KEYS.guestName);
  if (!guestName) {
    guestName = generateGuestName();
    localStorage.setItem(STORAGE_KEYS.guestName, guestName);
  }
  return { userId, guestName };
}

// ---------------------------------------------------------------------------
// PSV-1890 — chosen pilot name (entry screen). Separate from the legacy
// auto-generated guestName: the server is authoritative (SetName RPC +
// Player.DisplayName); this cache only pre-fills the entry input and
// survives reloads.
// ---------------------------------------------------------------------------

const PILOT_NAME_KEY = 'starblast.pilotName';

/**
 * `?pilot=` QA tabs get a per-tab (sessionStorage) name slot so two
 * windows on one machine can fly under different names — localStorage
 * would silently share one name between them (the same trap the
 * userId had, PSV-1726).
 */
function pilotNameStorage(): { store: Storage; key: string } {
  const tag = new URLSearchParams(window.location.search).get('pilot');
  if (tag !== null && tag !== '') {
    return { store: sessionStorage, key: `starblast.pilot.${tag}.name` };
  }
  return { store: localStorage, key: PILOT_NAME_KEY };
}

export function readPilotName(): string | null {
  const { store, key } = pilotNameStorage();
  const v = store.getItem(key);
  return v !== null && v.trim() !== '' ? v : null;
}

export function writePilotName(name: string): void {
  const { store, key } = pilotNameStorage();
  store.setItem(key, name);
}

/** PSV-1890 — first-session flag for the onboarding hint toasts. */
const HINTS_SHOWN_KEY = 'starblast.hintsShown';

export function hintsAlreadyShown(): boolean {
  return localStorage.getItem(HINTS_SHOWN_KEY) === '1';
}

export function markHintsShown(): void {
  localStorage.setItem(HINTS_SHOWN_KEY, '1');
}
